/**
 * Protected Route Component
 * 
 * Ensures that routes are only accessible to authenticated users.
 * Redirects to the login page if the user is not authenticated.
 */

import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredTenantId?: string;
}

/**
 * Protected Route component
 * 
 * Wraps routes that should only be accessible to authenticated users.
 * Optionally checks for a specific tenant ID.
 * 
 * @param children - The components to render if authenticated
 * @param requiredTenantId - Optional tenant ID to check
 */
export function ProtectedRoute({ 
  children, 
  requiredTenantId 
}: ProtectedRouteProps): JSX.Element {
  const { isAuthenticated, isLoading, tenantId } = useAuth();
  const location = useLocation();

  // Show loading state while authentication is being checked
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check tenant ID if required
  if (requiredTenantId && tenantId !== requiredTenantId) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground">
            You don't have access to this resource. It belongs to a different organization.
          </p>
          <button 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
            onClick={() => window.location.href = '/workspace'}
          >
            Return to Workspace
          </button>
        </div>
      </div>
    );
  }

  // Render children if authenticated and tenant check passes
  return <>{children}</>;
}
